'use client';

import { useId, useMemo } from 'react';
import type { ViewerGuidanceAnchor } from './types';
import { locateAnchorPages } from './locate-anchor';

interface GuidanceRailProps {
  anchors: ViewerGuidanceAnchor[];
  pageTexts: string[];
  onJump: (page: number) => void;
  /** Collapsed to a slim strip. Owned by the panel (persisted per browser). */
  collapsed?: boolean;
  /** Hide / Show. The collapse controls render only when this is supplied. */
  onToggleCollapsed?: () => void;
}

/**
 * Guidance beside the document rather than inside it.
 *
 * The old HTML preview hung clickable <sup> markers in the prose; a PDF page is
 * a canvas plus a text layer and has nowhere to put them. Listing anchors here,
 * each jumping to its page, preserves the capability the markers provided —
 * "which guidance drove this wording, and where is it" — in a form the renderer
 * can actually support.
 *
 * WHICH AUTHORITY DROVE THIS PARAGRAPH (spec decision D4). Each entry cites ITS
 * sources by name, with the guidance points citing them underneath — the
 * question a caseworker actually asks is "why does this clause say that?", and
 * "2 source(s)" is a number, not an answer. The retired markers answered it by
 * resolving each paragraph's `data-sources` against `guidanceBrief.sources`;
 * the case-level `GuidanceProvenancePanel` lists document-level sources with no
 * per-paragraph mapping, so nothing else covers it.
 *
 * The resolution happens in the CALLER (decision D2 — the viewer resolves
 * nothing): anchors arrive with `sourceLabels`/`sourcePoints` already filled
 * in. When a caller has no brief to resolve against, both are absent and this
 * falls back to the bare count rather than showing nothing.
 *
 * Page resolution is memoised on `[anchors, pageTexts]` rather than computed
 * inline per anchor during render: this rail lives inside the panel, and the
 * panel re-renders on every `setPage` — which `PdfCanvas` drives on every
 * scroll tick via `onVisiblePageChange`. Re-locating every anchor (each a
 * full-text normalise-and-scan over every page) on every scroll tick is the
 * exact cost virtualisation exists to avoid on a long document.
 * `locateAnchorPages` hoists page normalisation out of the per-anchor loop,
 * so the memo only recomputes when the anchors list or the extracted page
 * text actually changes (a new document, or extraction finishing) — never
 * on scroll, since `pageTexts` keeps its identity across `setPage` calls.
 */
export function GuidanceRail({
  anchors, pageTexts, onJump, collapsed = false, onToggleCollapsed,
}: GuidanceRailProps) {
  const railId = useId();
  const pages = useMemo(() => locateAnchorPages(anchors, pageTexts), [anchors, pageTexts]);

  if (anchors.length === 0) return null;

  if (collapsed && onToggleCollapsed) {
    return (
      <aside className="flex w-9 shrink-0 flex-col items-center border-l border-line bg-surface py-3">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={`Show guidance (${anchors.length})`}
          aria-expanded="false"
          className="rounded px-1 py-2 text-[11px] font-medium uppercase tracking-wide text-muted transition-colors hover:bg-line-firm hover:text-ink [writing-mode:vertical-rl]"
        >
          Show guidance ({anchors.length})
        </button>
      </aside>
    );
  }

  return (
    <aside className="w-64 shrink-0 overflow-y-auto border-l border-line bg-surface p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted">
          Drafted with guidance
        </p>
        {onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label="Hide guidance"
            aria-expanded="true"
            aria-controls={railId}
            className="rounded px-1.5 py-0.5 text-[11px] text-muted transition-colors hover:bg-line-firm hover:text-ink"
          >
            Hide
          </button>
        )}
      </div>
      <ul id={railId} className="space-y-2">
        {anchors.map((a, i) => {
          const page = pages[i] ?? null;
          const labels = a.sourceLabels?.filter(Boolean) ?? [];
          const points = a.sourcePoints?.filter(Boolean) ?? [];
          return (
            <li
              key={`${a.sectionKey}-${a.paragraphIndex}-${i}`}
              className="overflow-hidden rounded-md border border-sky-200 bg-sky-50/60"
            >
              <button
                type="button"
                disabled={page === null}
                onClick={() => page !== null && onJump(page)}
                className="w-full px-2.5 py-2 text-left text-[11px] text-content transition-colors enabled:hover:bg-sky-50 disabled:cursor-default disabled:opacity-70"
              >
                <span className="line-clamp-3 block">{a.text}</span>
                <span className="mt-1 block text-[10px] text-muted">
                  {/* No resolved names (a caller with no brief) — fall back to
                      the count, so the entry still says how much authority sits
                      behind it. */}
                  {labels.length === 0 &&
                    `${a.sourceIds.length} source${a.sourceIds.length === 1 ? '' : 's'}`}
                  {labels.length === 0 && ' · '}
                  {page !== null ? `page ${page}` : 'location not found'}
                </span>
              </button>

              {(labels.length > 0 || points.length > 0) && (
                <div className="border-t border-sky-200/70 bg-surface/60 px-2.5 py-1.5">
                  {labels.length > 0 && (
                    <ul className="space-y-0.5">
                      {labels.map((label, j) => (
                        <li
                          key={`${label}-${j}`}
                          className="text-[10px] font-medium leading-snug text-sky-800"
                        >
                          {label}
                        </li>
                      ))}
                    </ul>
                  )}
                  {points.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {points.map((point, j) => (
                        <li
                          key={`${point.slice(0, 24)}-${j}`}
                          className="line-clamp-2 text-[10px] leading-snug text-muted"
                        >
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
